import AppErrorLayout from 'components/Layouts/AppErrorLayout';
import AppLoadingLayout from 'components/Layouts/AppLoadingLayout';
import { ListContext, ListProvider } from 'components/ListsProvider';
import { LoginProvider } from 'components/LoginProvider';
import useOrganization from 'hooks/useOrganization';
import LoginPage from 'pages/LoginPage';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useLocalStore from 'store/localStore';
import useLoginStore from 'store/loginStore';
import { prefixRelativeOrg } from 'utils/prefixRelativeOrg';

const LoginWrapper = ({ roles: restrictToRoles, children }) => {
    const location = useLocation();
    const [wasLoggedIn, setWasLoggedIn] = useState(false);
    const isLoggedIn = useLoginStore(s => s.isLoggedIn);
    const user = useLoginStore(s => s.user);
    const { organization } = useOrganization();
    const currentRole = useLoginStore(s => s.getCurrentRole)();
    const setCurrentRole = useLoginStore(s => s.setCurrentRole);
    const allUserRoles = useLoginStore(s => s.roles);
    const hasRequiredRole = !restrictToRoles || (allUserRoles && restrictToRoles.some(role => allUserRoles.includes(role)));

    // Role, ktere uzivatel ma A route je povoluje.
    const matchedRoles = (restrictToRoles && allUserRoles)
        ? restrictToRoles.filter(role => allUserRoles.includes(role))
        : null;

    // Efekt nize umi `currentRole` prepnout, jenze efekty bezi az PO vykresleni deti.
    // Deti by se tedy stihly namountovat se starou (nebo jeste zadnou) roli a
    // odvozovaly by z ni chovani - napr. Player.js z `isTeamManager` pocita, jestli
    // vubec ukazat vyber tymu. Dokud role nesedi na route, radeji ukazeme loading.
    const roleIsSettled = !restrictToRoles || (matchedRoles != null && matchedRoles.includes(currentRole));
    const navigate = useNavigate();
    const { orgId: orgSlug } = useParams();
    const lastOrganizationId = useLocalStore(state => state.ORGANIZATION_ID);
    const fullPath = location.pathname + location.search;

    useEffect(() => {
        if (isLoggedIn) {
            setWasLoggedIn(true);
            
            // redirect to the last used (or default) organization if organization slug is missing from URL
            if (!orgSlug && user?.Organizations?.length > 0) {
                const org = user?.Organizations.find(o => o.organizationId == lastOrganizationId) ?? user?.Organizations[0];
                navigate(prefixRelativeOrg(org?.urlSlug, fullPath));
            }
        } else {
            // redirect to / after logout - to prevent 403 forbidden after login as another user
            if (wasLoggedIn) {
                navigate('/');
            }
        }
    }, [isLoggedIn, orgSlug, user, navigate, wasLoggedIn, lastOrganizationId]);

    useEffect(() => {
        if (restrictToRoles) {   // need to check current role
            if (allUserRoles) {     // user roles have been loaded
                
                const matchedRoles = restrictToRoles.filter(role => allUserRoles.includes(role));  // roles that both user has and the path is allowed for

                if (matchedRoles.includes(currentRole)) {
                    // current role in in the allowed list -> no need to change it (typical for common pages, such as Profile)
                    return;
                }

                // we pick the highest ranking role out of the allowed ones
                if (matchedRoles.includes('SYSTEM_ADMIN')) {
                    setCurrentRole('SYSTEM_ADMIN');
                } else if (matchedRoles.includes('ADMIN')) {
                    setCurrentRole('ADMIN');
                } else if (matchedRoles.includes('TEAM_MANAGER')) {
                    setCurrentRole('TEAM_MANAGER');
                } else if (matchedRoles.includes('PLAYER')) {
                    setCurrentRole('PLAYER');
                }
            }
        }
    }, [restrictToRoles, allUserRoles, currentRole, setCurrentRole]);

    // console.log('isLoggedIn', isLoggedIn);
    // console.log('organization', organization);
    // console.log('hasRequiredRole', hasRequiredRole);
    // console.log('userRoles', userRoles);
    // console.log('roles', roles);

    return (
        <LoginProvider>
            {(isLoggedIn === null) && <AppLoadingLayout delay={250} />}
            {(isLoggedIn === false) && <LoginPage />}
            {(isLoggedIn === true && organization == null) &&
                <>
                    {user?.Organizations?.length > 0
                        ? (orgSlug && <AppErrorLayout type="NO_ACCESS_TO_ORGANIZATION" />)
                        : <AppErrorLayout type="NO_ORGANIZATIONS" />
                    }
                </>
            }
            {(isLoggedIn === true && organization && hasRequiredRole && !roleIsSettled) &&
                <AppLoadingLayout />
            }
            {(isLoggedIn === true && organization && hasRequiredRole && roleIsSettled) &&
                <ListProvider>
                    <ListContext.Consumer>
                        {({ isLoading, isError }) => <>
                            {isLoading && <AppLoadingLayout />}
                            {isError && <AppErrorLayout />}
                            {(!isLoading && !isError) && <>{children}</>}
                        </>}
                    </ListContext.Consumer>
                </ListProvider>
            }
            {(isLoggedIn === true && organization && !hasRequiredRole) &&
                <AppErrorLayout type="403" />
            }
        </LoginProvider>
    )
}

export default LoginWrapper